import { useState, useEffect } from "react";
import AdminLayout from "../../components/AdminLayout";
import VerifiedBadge from "../../components/VerifiedBadge";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Edit2, Trash2, Search, Sparkles, CheckCircle2, X, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminArtists() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (search) params.set("search", search);
      const r = await axios.get(`${API}/artists?${params}`);
      setArtists(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar artistas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const onAutoSeed = async () => {
    if (!window.confirm("Importar todos artistas únicos dos CDs/DVDs cadastrados?")) return;
    try {
      const r = await axios.post(`${API}/admin/artists/auto-seed`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${r.data.created} artistas criados (total: ${r.data.total_artists})`);
      load();
    } catch (e) {
      toast.error("Erro no auto-seed");
    }
  };

  const [generatingBios, setGeneratingBios] = useState(false);
  const onGenerateBiosBulk = async () => {
    const overwrite = window.confirm(
      "Gerar bios com IA para artistas SEM bio.\n\nClique OK para gerar só faltantes.\nCancele e me pergunte de novo segurando Shift se quiser sobrescrever tudo."
    );
    if (!overwrite) return;
    setGeneratingBios(true);
    toast.info("Gerando bios... isso pode levar de 30s a 2 min", { duration: 4000 });
    try {
      const r = await axios.post(
        `${API}/admin/artists/generate-bios-bulk`,
        { overwrite: false },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 },
      );
      toast.success(`Bios geradas: ${r.data.generated}/${r.data.total_candidates}`);
      if (r.data.failed?.length) {
        toast.error(`${r.data.failed.length} falharam (ver console)`);
        console.error("Bios failed:", r.data.failed);
      }
      load();
    } catch (e) {
      toast.error("Erro ao gerar bios em massa");
      console.error(e);
    } finally {
      setGeneratingBios(false);
    }
  };

  const onGenerateBio = async (a) => {
    if (!window.confirm(`Gerar bio com IA para "${a.name}"?${a.bio ? "\n\nA bio atual será SUBSTITUÍDA." : ""}`)) return;
    try {
      toast.info("Gerando bio...", { duration: 2000 });
      await axios.post(
        `${API}/admin/artists/${a.id}/generate-bio`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 },
      );
      toast.success(`Bio de "${a.name}" gerada!`);
      load();
    } catch (e) {
      toast.error("Erro ao gerar bio");
    }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Excluir o artista "${a.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/artists/${a.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Excluído");
      load();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  const onToggleVerified = async (a) => {
    try {
      await axios.put(`${API}/admin/artists/${a.id}`, { verified: !a.verified }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(a.verified ? "Verificação removida" : "Marcado como verificado");
      load();
    } catch (e) {
      toast.error("Erro");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Artistas</h1>
            <p className="text-zinc-400 text-sm">Gerencie perfis oficiais, selo de verificação e biografia.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={onGenerateBiosBulk}
              disabled={generatingBios}
              className="flex items-center gap-2 px-4 py-2 bg-violet-500 text-white rounded-lg font-bold text-sm hover:bg-violet-400 disabled:opacity-60 disabled:cursor-wait"
              data-testid="generate-bios-bulk-btn"
              title="Gera biografias com IA para artistas que ainda não têm 'bio' preenchida"
            >
              <Wand2 size={16} /> {generatingBios ? "Gerando..." : "Gerar bios IA"}
            </button>
            <button onClick={onAutoSeed} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-black rounded-lg font-bold text-sm hover:bg-emerald-400">
              <Sparkles size={16} /> Importar dos CDs
            </button>
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#FF007F] text-white rounded-lg font-bold text-sm hover:bg-[#FF3399]">
              <Plus size={16} /> Novo artista
            </button>
          </div>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
          <input
            type="search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm focus:border-[#FF007F] focus:outline-none"
          />
        </div>

        {loading ? (
          <p className="text-zinc-500">Carregando...</p>
        ) : (
          <>
            {/* Mobile: cards verticais */}
            <div className="sm:hidden space-y-2">
              {artists.map((a) => (
                <div key={a.id} className="bg-zinc-900 border border-white/10 rounded-xl p-3 flex items-center gap-3" data-testid={`mobile-artist-${a.slug}`}>
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0">
                    {a.photo_url ? (
                      <img src={a.photo_url.startsWith("http") ? a.photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.photo_url}`} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-500">{a.name?.charAt(0)}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm truncate flex items-center gap-1">
                      {a.name}
                      {a.verified && <VerifiedBadge size={12} />}
                    </p>
                    <p className="text-zinc-500 text-[11px] font-mono truncate">{a.slug}</p>
                    <p className="text-zinc-400 text-[11px]">{a.followers_count?.toLocaleString("pt-BR") || 0} seguidores</p>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => onToggleVerified(a)} className={`text-[10px] px-2 py-1 rounded font-bold ${a.verified ? "bg-[#FF007F]/20 text-[#FF007F]" : "bg-zinc-800 text-zinc-400"}`} data-testid={`toggle-verified-mobile-${a.slug}`}>
                      {a.verified ? "✓" : "Verificar"}
                    </button>
                    <div className="flex gap-1">
                      <button
                        onClick={() => onGenerateBio(a)}
                        className="p-1.5 hover:bg-violet-500/20 rounded text-zinc-400 hover:text-violet-400"
                        title="Gerar bio com IA"
                        data-testid={`gen-bio-mobile-${a.slug}`}
                      >
                        <Wand2 size={12} />
                      </button>
                      <button onClick={() => { setEditing(a); setShowForm(true); }} className="p-1.5 hover:bg-white/10 rounded text-zinc-400">
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => onDelete(a)} className="p-1.5 hover:bg-red-500/20 rounded text-zinc-400 hover:text-red-400">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {artists.length === 0 && <p className="text-center text-zinc-500 py-8">Nenhum artista cadastrado.</p>}
            </div>

            {/* Desktop: tabela */}
            <div className="hidden sm:block overflow-x-auto bg-zinc-900 border border-white/10 rounded-xl">
              <table className="w-full text-sm">
                <thead className="text-left bg-black/40">
                  <tr>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500">Foto</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500">Nome</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500">Slug</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500">Verificado</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500">Seguidores</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {artists.map((a) => (
                    <tr key={a.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-4 py-2">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-800">
                          {a.photo_url ? (
                            <img src={a.photo_url.startsWith("http") ? a.photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.photo_url}`} alt={a.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs">{a.name?.charAt(0)}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 font-bold text-white">
                        <span className="flex items-center gap-1.5">
                          {a.name}
                          {a.verified && <VerifiedBadge size={14} />}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-zinc-500 font-mono text-xs">{a.slug}</td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => onToggleVerified(a)}
                          data-testid={`toggle-verified-${a.slug}`}
                          className={`text-xs px-2 py-1 rounded-full font-bold ${a.verified ? "bg-[#FF007F]/20 text-[#FF007F]" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
                        >
                          {a.verified ? "✓ Verificado" : "Marcar"}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-zinc-300">{a.followers_count?.toLocaleString("pt-BR") || 0}</td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          <button
                            onClick={() => onGenerateBio(a)}
                            className="p-2 hover:bg-violet-500/20 rounded text-zinc-400 hover:text-violet-400"
                            title="Gerar bio com IA"
                            data-testid={`gen-bio-${a.slug}`}
                          >
                            <Wand2 size={14} />
                          </button>
                          <button onClick={() => { setEditing(a); setShowForm(true); }} className="p-2 hover:bg-white/10 rounded text-zinc-400 hover:text-white">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => onDelete(a)} className="p-2 hover:bg-red-500/20 rounded text-zinc-400 hover:text-red-400">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {artists.length === 0 && <p className="text-center text-zinc-500 py-12">Nenhum artista cadastrado.</p>}
            </div>
          </>
        )}

        {showForm && <ArtistForm artist={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      </div>
    </AdminLayout>
  );
}

function ArtistForm({ artist, onClose, onSaved }) {
  const token = localStorage.getItem("token");
  const [form, setForm] = useState({
    name: artist?.name || "",
    photo_url: artist?.photo_url || "",
    bio: artist?.bio || "",
    city: artist?.city || "",
    verified: artist?.verified || false,
    genres: (artist?.genres || ["Arrocha"]).join(", "),
    social: {
      instagram: artist?.social?.instagram || artist?.instagram || "",
      facebook: artist?.social?.facebook || artist?.facebook || "",
      youtube: artist?.social?.youtube || artist?.youtube || "",
      website: artist?.social?.website || artist?.website || "",
      tiktok: artist?.social?.tiktok || artist?.tiktok || "",
      twitter: artist?.social?.twitter || artist?.twitter || "",
      spotify: artist?.social?.spotify || artist?.spotify || "",
    },
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", f);
      const r = await axios.post(`${API}/admin/artists/upload-photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, photo_url: r.data.url }));
      toast.success("Foto carregada");
    } catch {
      toast.error("Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nome obrigatório");
    setSaving(true);
    try {
      const payload = {
        ...form,
        genres: form.genres.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (artist) {
        await axios.put(`${API}/admin/artists/${artist.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Atualizado");
      } else {
        await axios.post(`${API}/admin/artists`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Artista criado");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="bg-zinc-900 rounded-2xl border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="font-bold text-lg text-white">{artist ? "Editar artista" : "Novo artista"}</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Nome *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#FF007F] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Foto</label>
            <div className="flex items-center gap-3">
              {form.photo_url && (
                <img src={form.photo_url.startsWith("http") ? form.photo_url : `${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`} alt="preview" className="w-16 h-16 rounded-full object-cover" />
              )}
              <label className="px-4 py-2 bg-zinc-800 rounded-lg text-sm cursor-pointer hover:bg-zinc-700">
                {uploading ? "Enviando..." : "Carregar foto"}
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
              </label>
              {form.photo_url && (
                <button type="button" onClick={() => setForm({ ...form, photo_url: "" })} className="text-xs text-zinc-500 hover:text-red-400">Remover</button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Biografia</label>
            <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#FF007F] focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Cidade</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#FF007F] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Gêneros (vírgula)</label>
              <input value={form.genres} onChange={(e) => setForm({ ...form, genres: e.target.value })} placeholder="Arrocha, Sofrência" className="w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#FF007F] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Redes Sociais</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={form.social.instagram} onChange={(e) => setForm({ ...form, social: { ...form.social, instagram: e.target.value } })} placeholder="Instagram (@handle ou URL)" className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none" data-testid="admin-social-instagram" />
              <input value={form.social.youtube} onChange={(e) => setForm({ ...form, social: { ...form.social, youtube: e.target.value } })} placeholder="YouTube (@canal ou URL)" className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none" data-testid="admin-social-youtube" />
              <input value={form.social.tiktok} onChange={(e) => setForm({ ...form, social: { ...form.social, tiktok: e.target.value } })} placeholder="TikTok (@handle ou URL)" className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none" data-testid="admin-social-tiktok" />
              <input value={form.social.twitter} onChange={(e) => setForm({ ...form, social: { ...form.social, twitter: e.target.value } })} placeholder="X / Twitter (@handle ou URL)" className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none" data-testid="admin-social-twitter" />
              <input value={form.social.facebook} onChange={(e) => setForm({ ...form, social: { ...form.social, facebook: e.target.value } })} placeholder="Facebook (handle ou URL)" className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none" data-testid="admin-social-facebook" />
              <input value={form.social.spotify} onChange={(e) => setForm({ ...form, social: { ...form.social, spotify: e.target.value } })} placeholder="Spotify Artist (ID ou URL)" className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none" data-testid="admin-social-spotify" />
              <input value={form.social.website} onChange={(e) => setForm({ ...form, social: { ...form.social, website: e.target.value } })} placeholder="Site oficial" className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none sm:col-span-2" data-testid="admin-social-website" />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} className="w-4 h-4 accent-[#FF007F]" />
            <CheckCircle2 size={16} className={form.verified ? "text-[#FF007F]" : "text-zinc-500"} />
            <span className="text-sm">Marcar como verificado (selo oficial)</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-zinc-900">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white">Cancelar</button>
          <button type="submit" disabled={saving} className="px-6 py-2 bg-[#FF007F] text-white rounded-lg font-bold text-sm hover:bg-[#FF3399] disabled:opacity-60">
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}
